import type { Location } from "react-router-dom";
import { buildAppWindowUrl } from "@/modules/window/build-url";

/**
 *
 * After a user completes or cancels a Stripe flow (checkout or billing portal),
 * Stripe redirects them back to our application using the return URLs we provided.
 * Since Stripe doesn't maintain session state across redirects, we use URL query
 * parameters to communicate the result of the flow back to our app.
 *
 * For example:
 * - After successful checkout: https://app.mem.ai/?stripe_success=1&session_id=cs_...
 * - After cancelled checkout: https://app.mem.ai/?stripe_cancel=1
 * - After billing portal: https://app.mem.ai/?stripe_billing_return=1&has_mem_pro=1
 *
 * Convention: We use "1" to indicate truthiness for boolean parameters.
 * This convention is used throughout the codebase for consistency and simplicity,
 * avoiding the need to parse string values like "true" or "false".
 *
 * The parseStripeQueryParams() function below extracts these parameters and
 * converts them into a structured object that the application can use to
 * determine what UI state to show and what side effects to trigger.
 */

/**
 * Stripe-related URL query parameter constants.
 * These are used for tracking the result of Stripe checkout/billing flows.
 */
export const StripeQueryParams = {
  /**
   * Set to "1" when returning from successful Stripe checkout.
   */
  STRIPE_SUCCESS: "stripe_success",

  /**
   * Contains the Stripe checkout session ID (starts with "cs_").
   * Included in the success URL after checkout completion.
   */
  SESSION_ID: "session_id",

  /**
   * Set to "1" when user cancels the Stripe checkout flow.
   */
  STRIPE_CANCEL: "stripe_cancel",

  /**
   * Set to "1" when returning from the Stripe billing portal.
   */
  STRIPE_BILLING_RETURN: "stripe_billing_return",

  /**
   * Set to "1" when user already has Mem Pro subscription.
   * Used in combination with STRIPE_BILLING_RETURN.
   */
  HAS_MEM_PRO: "has_mem_pro",
} as const;

/**
 * Helper functions for working with Stripe query parameters.
 */

/**
 * Checks if a query parameter is set to "1".
 */
function isParamTrue(value: string | null): boolean {
  return value === "1";
}

/**
 * Checks if a string is a valid Stripe checkout session ID.
 * Stripe checkout session IDs start with "cs_".
 */
function isStripeCheckoutSessionId(value: string | null): boolean {
  return value !== null && value.startsWith("cs_");
}

/**
 * Parses Stripe-related query parameters from a Location object.
 */
export function parseStripeQueryParams(location: Location) {
  const params = new URLSearchParams(location.search);

  const stripeSuccess = params.get(StripeQueryParams.STRIPE_SUCCESS);
  const sessionId = params.get(StripeQueryParams.SESSION_ID);
  const stripeCancel = params.get(StripeQueryParams.STRIPE_CANCEL);
  const stripeBillingReturn = params.get(StripeQueryParams.STRIPE_BILLING_RETURN);
  const hasMemPro = params.get(StripeQueryParams.HAS_MEM_PRO);

  return {
    /**
     * True if the user successfully completed Stripe checkout.
     * Checks both stripe_success=1 and presence of a valid session_id.
     */
    isCheckoutSuccess: isParamTrue(stripeSuccess) || isStripeCheckoutSessionId(sessionId),

    /**
     * True if the user cancelled Stripe checkout.
     */
    isCheckoutCancel: isParamTrue(stripeCancel),

    /**
     * True if returning from Stripe billing portal.
     */
    isBillingReturn: isParamTrue(stripeBillingReturn),

    /**
     * True if the user already has Mem Pro (used with billing portal return).
     */
    hasMemPro: isParamTrue(hasMemPro),

    /**
     * The Stripe checkout session ID, if present.
     */
    sessionId,

    /**
     * List of query param keys to remove from the URL.
     */
    paramsToRemove: [
      StripeQueryParams.STRIPE_SUCCESS,
      StripeQueryParams.SESSION_ID,
      StripeQueryParams.STRIPE_CANCEL,
      StripeQueryParams.STRIPE_BILLING_RETURN,
      StripeQueryParams.HAS_MEM_PRO,
    ].filter(Boolean) as string[],
  };
}

/**
 * Helper function to append query parameters to a URL.
 */
function appendQueryParams(baseUrl: string, params: Record<string, string>): string {
  const url = new URL(baseUrl);
  Object.entries(params).forEach(([key, value]) => {
    url.searchParams.set(key, value);
  });
  return url.toString();
}

interface BuildStripeUrlParams {
  location: Location;
}

/**
 * Builds the base return URL from a location.
 * This is the URL the user will return to after Stripe flows.
 */
export function buildStripeReturnUrl({ location }: BuildStripeUrlParams): string {
  return buildAppWindowUrl({
    path: location.pathname + (location.search || ""),
  }).toString();
}

/**
 * Builds the success URL for Stripe checkout.
 * Includes stripe_success=1 and session_id placeholder.
 */
export function buildStripeSuccessUrl({ location }: BuildStripeUrlParams): string {
  const baseUrl = buildStripeReturnUrl({ location });
  return appendQueryParams(baseUrl, {
    [StripeQueryParams.STRIPE_SUCCESS]: "1",
    [StripeQueryParams.SESSION_ID]: "{CHECKOUT_SESSION_ID}",
  });
}

/**
 * Builds the cancel URL for Stripe checkout.
 * Includes stripe_cancel=1.
 */
export function buildStripeCancelUrl({ location }: BuildStripeUrlParams): string {
  const baseUrl = buildStripeReturnUrl({ location });
  return appendQueryParams(baseUrl, {
    [StripeQueryParams.STRIPE_CANCEL]: "1",
  });
}

interface BuildStripeBillingReturnUrlParams extends BuildStripeUrlParams {
  /**
   * Whether to include the has_mem_pro=1 parameter.
   */
  hasMemPro?: boolean;
}

/**
 * Builds the return URL for Stripe billing portal.
 * Optionally includes has_mem_pro=1.
 */
export function buildStripeBillingReturnUrl({
  location,
  hasMemPro = false,
}: BuildStripeBillingReturnUrlParams): string {
  const baseUrl = buildStripeReturnUrl({ location });
  const params: Record<string, string> = {
    [StripeQueryParams.STRIPE_BILLING_RETURN]: "1",
  };

  if (hasMemPro) {
    params[StripeQueryParams.HAS_MEM_PRO] = "1";
  }

  return appendQueryParams(baseUrl, params);
}
